<template>
  <pdl-loading top :is-loading="appLoading">
    <div id="project-one-app">
      <div class="project-one-main-layout">
        <nav v-if="hasNavigation">
          <project-one-nav :base-properties="baseProperties"></project-one-nav>
        </nav>

        <main id="project-one-main-content">
          <pdl-loading :is-loading="isPageLoading" :page="true">
            <!-- If b2b, render the toolbar layout -->
            <div v-if="b2b && !appLoading" :class="{'project-one-toolbar-layout': b2b}">
              <!-- Component for toolbar, only to be shown on B2B -->
              <project-one-toolbar :base-properties="baseProperties" />

              <project-one-toolbar-drawer :base-properties="baseProperties">
                <template v-slot:content>
                  <!-- See /projectOne/router/routes.js for the possible views depending on the url/path -->
                  <router-view :key="$route.fullPath" class="view" :router-base-properties="baseProperties" />
                </template>
              </project-one-toolbar-drawer>
            </div>

            <!-- Else render only router-view -->
            <!-- See /projectOne/router/routes.js for the possible views depending on the url/path -->
            <router-view
              v-else-if="!appLoading"
              :key="$route.fullPath"
              class="view"
              :router-base-properties="baseProperties"
            />
          </pdl-loading>
        </main>

        <project-one-global-modals :base-properties="baseProperties" />
      </div>
    </div>
  </pdl-loading>
</template>

<script>
import {mapActions, mapMutations, mapState, mapGetters} from 'vuex';
import {PdlLoading} from '@pedal/pdl-loading';
import ProjectOneGlobalModals from '@/projectOne/components/shared/ProjectOneGlobalModals';
import ProjectOneNav from '@/projectOne/components/navigation/ProjectOneNav';
import ProjectOneToolbar from '@/projectOne/components/toolbar/ProjectOneToolbar';
import ProjectOneToolbarDrawer from '@/projectOne/components/toolbar/ProjectOneToolbarDrawer';

export default {
  components: {
    PdlLoading,
    ProjectOneGlobalModals,
    ProjectOneNav,
    ProjectOneToolbar,
    ProjectOneToolbarDrawer,
  },

  props: {
    redirectUrl: {
      type: String,
      required: true,
      default: '',
    },
    isHidingPrices: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      buildId: null,
      appLoading: true,
      transitionName: 'slide',
      resizeTimeout: null,
      resizeDelay: 200,
    };
  },

  computed: {
    route() {
      return this.$route;
    },

    hasNavigation() {
      return this.$route.meta.nav;
    },

    ...mapGetters('backend', ['b2b']),

    ...mapState('projectOne', [
      'isPageLoading',
      'cachedRedirectUrlB2c',
      'cachedRedirectUrlB2b',
      'cachedRedirectUrlAnonymous',
      'cachedSelections',
      'cachedBuildId',
    ]),

    ...mapState('backend', ['isUserLoggedIn']),
  },

  watch: {
    route(to, from) {
      const toPosition = to.meta.position;
      const fromPosition = from.meta.position;
      this.transitionName = toPosition < fromPosition ? 'slide' : 'slideback';
    },
  },

  created() {
    if (this.cachedRedirectUrlB2c || this.cachedRedirectUrlB2b || this.cachedRedirectUrlAnonymous) {
      this.buildId = this.cachedBuildId;
      this.setSelections(this.cachedSelections);

      let url;

      if (!this.isUserLoggedIn && this.cachedRedirectUrlAnonymous) {
        url = this.cachedRedirectUrlAnonymous;
      } else if (this.b2b && this.cachedRedirectUrlB2b) {
        url = this.cachedRedirectUrlB2b;
      } else if (this.cachedRedirectUrlB2c) {
        url = this.cachedRedirectUrlB2c;
      }

      if (url) {
        this.clearCachedRedirectData();
        window.location.href = url;
      }
    } else {
      this.setRedirectUrl(this.redirectUrl);
      this.buildId = this.$route.params.buildId;
    }
    this.setRouter(this.$router);
    if (this.isHidingPrices) {
      this.setIsHidingPrices(this.isHidingPrices);
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.calculateApplicationHeight);
  },

  async mounted() {
    window.addEventListener('resize', this.calculateApplicationHeight);

    this.buildId?.includes('P-') ? this.loadBikeConfiguration() : this.loadBikePackage();

    await this.fetchBikeEta({buildId: this.$route.params.buildId});

    // We use nextTick here to ensure the entire app is rendered before we try to set the app height
    this.$nextTick(() => {
      this.calculateApplicationHeight();
    });
  },

  methods: {
    ...mapActions('projectOne', [
      'fetchBikeEta',
      'fetchBikeConfiguration',
      'fetchBikePackage',
      'updateActiveNavigationStep',
    ]),
    ...mapMutations('projectOne', [
      'setRedirectUrl',
      'calculateApplicationHeight',
      'clearCachedRedirectData',
      'setRouter',
      'setSelections',
      'setIsHidingPrices',
    ]),

    loadBikeConfiguration() {
      this.appLoading = true;
      if (this.buildId) this.fetchBikeConfiguration(this.buildId).finally(() => (this.appLoading = false));
    },

    loadBikePackage() {
      this.appLoading = true;
      if (this.buildId) this.fetchBikePackage(this.buildId).finally(() => (this.appLoading = false));
      else window.location.href = this.redirectUrl;
    },
  },
};
</script>

<style lang="scss" scoped>
#project-one-app {
  display: grid;

  @apply leading-normal; // TODO: remove once global html/body line height is determined

  .project-one-main-layout {
    display: grid;
    grid-template-rows: auto 1fr;
  }

  .project-one-toolbar-layout {
    display: grid;
    grid-template-columns: auto 1fr;
  }
}

// TODO: disabled due to application height issue, will be fixed in later story
.slide-leave-active,
.slide-enter-active {
  transition: 1s;
}

.slide-enter {
  transform: translate(100%, 0);
}

.slide-leave-to {
  transform: translate(-100%, 0);
}

.slideback-leave-active,
.slideback-enter-active {
  transition: 1s;
}

.slideback-enter {
  transform: translate(-100%, 0);
}

.slideback-leave-to {
  transform: translate(100%, 0);
}
</style>
